import {
	DaysOfWeekEnum,
	EventPublishingStateEnum,
	PrivacyTypesEnum,
	ScheduleRepeatTypesEnum,
	EventPolicyTypesEnum,
} from '../../enums';
import {
	EventToUserDto,
	FileUploadDto,
	OrderDto,
	FileDto,
	UserDto,
	CityDto,
	DiscountDto,
	InterestDto,
	EventToFitnessTypeDto,
	EventPriceDto,
	WaitlistedUserToEventDto,
	EventDonationToMembershipDto,
	CreateEventDonationToMembershipDto,
	UpdateEventDonationToMembershipDto,
	EventBundleDto,
	UpdateEventToFitnessTypeDto,
	CreateEventPriceDto,
	UpdateEventPriceDto,
	UpsertEventDiscountDto,
} from './';
import {
	ArrayMinSize,
	IsArray,
	IsBoolean,
	IsEnum,
	IsNotEmpty,
	IsOptional,
	IsString,
	IsUrl,
	Max,
	Min,
	ValidateIf,
	ValidateNested,
	ArrayNotEmpty,
	IsUUID,
	ArrayMaxSize,
	IsDateString,
	IsNumber,
	IsInt,
} from 'class-validator';
import { Type } from 'class-transformer';
import { isNumber } from 'lodash-es';
import { Base64AllowedMimeTypes, MaxBase64FileSize, IsAfter, IsIncludedIn } from '../../decorators';
import { ALLOWED_EVENT_AVATAR_MIME_TYPES } from '../../constants';
import { shiftScheduleRepeat } from '../../utils';
import { EventSponsorDto } from './event-sponsor.dto';
import type { EventIntakeFormSettings } from './intake-form-settings.dto';
import { EditEventSponsorDto } from '../../modules/events/models/input/edit-event-sponsor.dto';
import { EditEventLeaderDto } from '../../modules/events/models/input/edit-event-leader.dto';

export class EventIntegrationsDto {
	gtagId?: string;
	fbPixelId?: string;
	attnId?: string;
};

export type EventAdditionalData = {
	outOfCurrentScheduleButPreserved?: boolean;
	skipMobileRSVPModal?: boolean;
	allowKlarnaPayments?: boolean;
	customWebCheckoutMainColor?: string;
	customWebCheckoutButtonText?: string;
	hideEventDate?: boolean;
	isMembershipEvent?: boolean;
	tracking?: EventIntegrationsDto;
	isDescriptionInMarkdown?: boolean;
	ticketSalesStartUtcTimeIso?: string;
	subtitleText?: string;
	subtitleImageUrl?: string;
	priceNotIncludesTaxes?: boolean;
	avatarDominantColor?: string;
	orderConfirmationEmailHtmlAddon?: string;
};

export type EventDraftData = {
	coHostId?: string;
	lastStep?: number;
};

export class PrivacyTypeDto {
	public id: PrivacyTypesEnum;
	public name: string;
}

export class CreateScheduleRepeat {
	@IsOptional()
	@Min(0)
	public interval?: number;

	@IsEnum(ScheduleRepeatTypesEnum)
	@IsNotEmpty()
	public typeId: ScheduleRepeatTypesEnum;

	@ValidateIf((o) => o.typeId === ScheduleRepeatTypesEnum.Monthly && !isNumber(o.weekOfMonth) && !Array.isArray(o.dayOfWeekIds))
	@Min(-1)
	@Max(31)
	public dayOfMonth?: number;

	@ValidateIf((o) => o.typeId === ScheduleRepeatTypesEnum.Weekly
		|| (o.typeId === ScheduleRepeatTypesEnum.Monthly && !isNumber(o.dayOfMonth)))
	@ArrayNotEmpty()
	public dayOfWeekIds?: DaysOfWeekEnum[];

	@ValidateIf((o) => o.typeId === ScheduleRepeatTypesEnum.Monthly && !isNumber(o.dayOfMonth))
	@IsIncludedIn([-4, -3, -2, -1, 1, 2, 3, 4, 5])
	public weekOfMonth?: number;

	public endDate?: Date;
}

export class BaseEventDto {
	public publishingState: EventPublishingStateEnum;
	public policyType: EventPolicyTypesEnum;
	public isPaid: boolean;
	public isDonationBased: boolean;
	public name: string;
	public attendeesLimit?: number;
	public ticketReservationTimeMinutes?: number;
	public isRsvpListHidden: boolean;
	public addressLat: string;
	public addressLng: string;
	public addressName?: string;
	public isOnlineEvent: boolean;
	public isEventWithClasses?: boolean;
	public onlineUrl: string;
	public countryId?: string;
	public stateId?: string;
	public cityId?: string;
	public startDate: Date;
	public endDate?: Date;
	public allowCheckoutForNDaysAfterEventEnded?: number;
	public createdAt: Date;
	public updatedAt: Date;
	public deletedAt?: Date;
	public eventChatCid?: string;
	public privacyId: PrivacyTypesEnum;
	public description?: string;
	public subHeaderMarkdown?: string;
	public distance?: number | null;
	public privacy?: PrivacyTypeDto;
	public waiverUrl?: string;
	public enableEventTicketsPage: boolean;
	public fitnessTypeIds?: string[];
	public fitnessTypes?: EventToFitnessTypeDto[];
	public coHost?: UserDto;
	public city?: CityDto;

	public allowToPublishToOtherPlatforms: boolean;

	public discounts?: DiscountDto[];

	public basePriceTierId?: string;

	/** same as `event.price?: number` */
	public basePriceTierAmount?: number;

	/** @deprecated */
	public basePriceTierDiscounts?: DiscountDto[];

	// deprecated fields with compatibility
	/**
	 * @deprecated
	 *
	 * same as `event.basePriceTierAmount?: number`
	 */
	public price?: number;

	/** @deprecated */
	public fitnessTypeId: string;

	/** @deprecated */
	public masteryLevel: number;

	/** @deprecated */
	public fitnessType?: InterestDto;
}

export class EventDto extends BaseEventDto {
	public id: string;
	public alias: string;
	public baseId: string;
	public actualFrom: Date;
	public actualTo: Date;
	public authorId: string;
	public rrule: string;
	public isSingleEvent?: boolean;
	public calculated_is_serial?: boolean;
	public prevEventId?: string;
	public tzid: string;
	public scheduleRepeatId: string;
	public author?: UserDto;
	public isVerified: boolean;
	public participants?: EventToUserDto[];
	public participantsCount?: number;
	public scheduleRepeat?: ScheduleRepeatDto;
	public avatarId?: string;
	public eventPayments?: OrderDto[];
	public revenue?: number;
	public isEditorPick: boolean;
	public isSweatSchedule: boolean;
	public subParticipants?: EventToUserDto[];
	public subParticipantsCount?: number;
	public instancesCalculatedAt?: Date;
	public onlySingleAttendanceAllowed?: boolean;
	public isWaitlistEnabled: boolean;
	public intakeFormSettings?: EventIntakeFormSettings;
	public additionalData?: EventAdditionalData;
	public draftData?: EventDraftData;
	public prices?: EventPriceDto[];
	public minimalDonationAmount?: number;
	public images?: EventImageDto[];
	public eventSponsors?: EventSponsorDto[];
	public eventLeaders?: UserDto[];
	public eventBundles?: EventBundleDto[];
	public isCurrentUserFavourite?: boolean;

	public ticketsCount?: number;
	public isCurrentUserHasFreeAccess?: boolean;

	public eventDonationToMemberships?: EventDonationToMembershipDto[];

	public media?: FileDto[];
	public galleryFiles?: EventGalleryFileDto[];
}

export class EventInstanceDto extends EventDto {
	public instance: Date;
	public eventInstanceId?: string;
	public instanceEndDate?: Date;
	public shortLocalInstance: string;
	public deepLink?: string;
	public overwritingEventId?: string;
	public currentEventToUser?: Partial<EventToUserDto>;
	public currentWaitlistedUserToEvent?: WaitlistedUserToEventDto;
	public override onlySingleAttendanceAllowed?: boolean;
}

export class BaseEventInstanceDto {
	public baseId: string;
	public instance: Date | string;
}

export class ShortEventInstanceDto
	implements
		Pick<
			EventInstanceDto,
			| 'id'
			| 'baseId'
			| 'instance'
			| 'name'
			| 'alias'
			| 'shortLocalInstance'
			| 'avatarId'
			| 'privacyId'
			| 'isPaid'
			| 'isDonationBased'
			| 'tzid'
			| 'scheduleRepeatId'
			| 'scheduleRepeat'
		>
{
	public id: string;
	public baseId: string;
	public instance: Date;
	public name: string;
	public alias: string;
	public shortLocalInstance: string;
	public avatarId?: string;
	public privacyId: PrivacyTypesEnum;
	public tzid: string;
	public scheduleRepeatId: string;
	public scheduleRepeat?: ScheduleRepeatDto;
	public isPaid: boolean;
	public isDonationBased: boolean;
	public eventsCount?: number;
}

// TODO: refactor IsOptional fields
export class UpdateBaseEventDto {

	@IsOptional()
	@IsUUID()
	public id?: string;

	@IsOptional()
	@IsUUID()
	public coHostId?: string | null;

	@IsOptional()
	@IsEnum(EventPublishingStateEnum)
	public publishingState?: EventPublishingStateEnum;

	@IsOptional()
	@IsEnum(EventPolicyTypesEnum)
	public policyType?: EventPolicyTypesEnum;

	@IsOptional()
	public isPaid?: boolean;

	@IsOptional()
	public isDonationBased?: boolean;

	@IsOptional()
	@IsString()
	@IsNotEmpty({
		message: 'Event name should not be empty',
	})
	public name?: string;

	@IsOptional()
	@Min(1)
	public attendeesLimit?: number | null;

	@IsOptional()
	@IsBoolean()
	public isRsvpListHidden?: boolean;

	@IsOptional()
	@ValidateIf((o) => !o.isOnlineEvent)
	@IsNotEmpty()
	public addressLat?: string;

	@IsOptional()
	@ValidateIf((o) => !o.isOnlineEvent)
	@IsNotEmpty()
	public addressLng?: string;

	@IsOptional()
	@ValidateIf((o) => !o.isOnlineEvent)
	@IsString()
	@IsNotEmpty({ message: 'Please enter a valid address' })
	public addressName?: string;

	@IsOptional()
	@ValidateIf((o) => o.isOnlineEvent)
	@IsUrl({}, { message: '"Event URL" link should be a valid URL' })
	public onlineUrl?: string;

	/** @deprecated */
	@IsOptional()
	@IsString()
	public fitnessTypeId?: string;

	/** @deprecated */
	@IsOptional()
	@Min(0)
	@Max(3)
	public masteryLevel?: number;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => UpdateEventToFitnessTypeDto)
	public fitnessTypes?: UpdateEventToFitnessTypeDto[];

	@IsNotEmpty({
		message: 'Start date should not be empty',
	})
	public startDate?: Date;

	@IsOptional()
	public endDate?: Date;

	@IsOptional()
	public allowCheckoutForNDaysAfterEventEnded?: number;

	@IsOptional()
	@IsEnum(PrivacyTypesEnum)
	@IsNotEmpty()
	public privacyId?: PrivacyTypesEnum;

	@IsOptional()
	@IsString()
	public description?: string;

	@IsOptional()
	@IsString()
	public subHeaderMarkdown?: string;

	@IsOptional()
	@ValidateNested()
	@Type(() => FileUploadDto)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_EVENT_AVATAR_MIME_TYPES)
	public avatarImg?: FileUploadDto;

	public avatarId?: string;

	@IsOptional()
	@IsUrl({}, { message: '"Waiver" link should be a valid URL' })
	public waiverUrl?: string | null;

	@IsOptional()
	@IsBoolean()
	public isEditorPick?: boolean;

	@IsOptional()
	@IsBoolean()
	public isSweatSchedule?: boolean;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	public uploadImages?: FileUploadDto[];

	@IsOptional()
	@IsArray()
	@IsUUID(4, { each: true })
	public deleteImages?: string[];

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	public newFiles?: FileUploadDto[];

	@IsOptional()
	@IsArray()
	@IsString({ each: true })
	public removeFiles?: string[];

	public isOnlineEvent?: boolean;
	public authorId?: string;
	public invitedUserIds?: string[];
	public scheduleRepeat?: CreateScheduleRepeat;
	public tzid?: string;

	public static fromEvent(event?: BaseEventDto): UpdateBaseEventDto {
		const baseUpdateEventDto = new UpdateBaseEventDto();

		baseUpdateEventDto.policyType = event?.policyType ?? EventPolicyTypesEnum.NoRefunds;
		baseUpdateEventDto.publishingState = event?.publishingState ?? EventPublishingStateEnum.Published;
		baseUpdateEventDto.isPaid = event?.isPaid ?? false;
		baseUpdateEventDto.isDonationBased = event?.isDonationBased ?? false;
		baseUpdateEventDto.attendeesLimit = event?.attendeesLimit;
		baseUpdateEventDto.isRsvpListHidden = event?.isRsvpListHidden;
		baseUpdateEventDto.name = event?.name ?? '';
		baseUpdateEventDto.tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
		baseUpdateEventDto.addressLat = event?.addressLat ?? '';
		baseUpdateEventDto.addressLng = event?.addressLng ?? '';
		baseUpdateEventDto.addressName = event?.addressName ?? '';
		baseUpdateEventDto.isOnlineEvent = event?.isOnlineEvent ?? false;
		baseUpdateEventDto.onlineUrl = event?.onlineUrl ?? '';
		baseUpdateEventDto.fitnessTypeId = event?.fitnessTypeId;
		baseUpdateEventDto.masteryLevel = event?.masteryLevel;
		baseUpdateEventDto.startDate = event?.startDate ? new Date(event.startDate) : undefined;
		baseUpdateEventDto.endDate = event?.endDate ? new Date(event.endDate) : undefined;
		baseUpdateEventDto.privacyId = event?.privacyId ?? PrivacyTypesEnum.Public;
		baseUpdateEventDto.description = event?.description;
		baseUpdateEventDto.waiverUrl = event?.waiverUrl;

		return baseUpdateEventDto;
	}
}

export enum EventPeriodType {
	ThisAndFollowing = 'THIS_AND_FOLLOWING',
	OnlyThis = 'ONLY_THIS',
}

/** @deprecated Please use UpdateEventV2Dto instead  */
export class UpdateEventDto extends UpdateBaseEventDto {

	@IsOptional()
	@IsNumber()
	@IsInt()
	@Min(0)
	public minimalDonationAmount?: number;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => UpdateEventDonationToMembershipDto)
	public updateEventDonationToMemberships?: UpdateEventDonationToMembershipDto[];

	// upsert strategy
	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => EditEventSponsorDto)
	public sponsors?: EditEventSponsorDto[];

	// upsert strategy
	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => EditEventLeaderDto)
	public leaders?: EditEventLeaderDto[];

	@IsOptional()
	@IsArray()
	public eventBundleIds?: string[];

	@IsOptional()
	@IsBoolean()
	public onlySingleAttendanceAllowed?: boolean;

	@IsOptional()
	public intakeFormSettings?: EventIntakeFormSettings;

	@IsOptional()
	public additionalData?: EventAdditionalData;

	@IsOptional()
	public draftData?: EventDraftData | null;

	@IsOptional()
	@IsString()
	public override authorId?: string;

	@IsOptional()
	@IsArray()
	public override invitedUserIds?: string[];

	@IsOptional()
	@ValidateNested()
	@Type(() => CreateScheduleRepeat)
	public override scheduleRepeat?: CreateScheduleRepeat;

	public autoGenerateEventChat?: boolean;

	@IsOptional()
	public eventChatCid?: string | null;

	@IsOptional()
	@IsNotEmpty()
	@IsEnum(EventPeriodType)
	public type?: EventPeriodType;

	@IsOptional()
	@IsNotEmpty()
	public instance?: Date;

	@IsOptional()
	@IsBoolean()
	public isVerified?: boolean;

	@IsOptional()
	@IsBoolean()
	public isWaitlistEnabled?: boolean;

	@IsOptional()
	@IsArray()
	@Type(() => CreateEventPriceDto)
	@ValidateNested({ each: true })
	public createPrices?: CreateEventPriceDto[];

	@IsOptional()
	@IsArray()
	@Type(() => UpdateEventPriceDto)
	@ValidateNested({ each: true })
	public updatePrices?: UpdateEventPriceDto[];

	@IsOptional()
	@IsArray()
	public deletePricesIds?: string[];

	@IsOptional()
	@IsBoolean()
	public deleteAllPrices?: boolean;

	@IsOptional()
	@IsNumber()
	public price?: number;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => UpsertEventDiscountDto)
	public upsertDiscounts?: UpsertEventDiscountDto[];

	public static override fromEvent(event?: EventDto | EventInstanceDto) {
		const updateEventDto: UpdateEventDto = super.fromEvent(event);

		if (event?.scheduleRepeat) {
			const schedule = event.scheduleRepeat;
			updateEventDto.scheduleRepeat = new CreateScheduleRepeat();
			updateEventDto.scheduleRepeat.typeId = schedule.typeId;
			updateEventDto.scheduleRepeat.dayOfMonth = schedule.dayOfMonth;
			updateEventDto.scheduleRepeat.weekOfMonth = schedule.weekOfMonth;
			updateEventDto.scheduleRepeat.interval = schedule.interval;
			updateEventDto.scheduleRepeat.dayOfWeekIds = schedule.daysOfWeek?.map(({ id }) => id);
			updateEventDto.scheduleRepeat.endDate = schedule.endDate;

			if (event?.startDate) {
				updateEventDto.scheduleRepeat = shiftScheduleRepeat(
					new Date(event.startDate),
					updateEventDto.scheduleRepeat,
					true,
					event.tzid,
				);
			}
		} else {
			updateEventDto.scheduleRepeat = new CreateScheduleRepeat();
			updateEventDto.scheduleRepeat.typeId = ScheduleRepeatTypesEnum.Never;
		}

		updateEventDto.additionalData = event?.additionalData ?? {};
		updateEventDto.authorId = event?.authorId;
		updateEventDto.invitedUserIds = event?.participants?.map(({ userId }) => userId) ?? [];
		updateEventDto.tzid = event?.tzid;
		updateEventDto.isEditorPick = event?.isEditorPick ?? false;
		updateEventDto.isSweatSchedule = event?.isSweatSchedule ?? false;
		updateEventDto.eventChatCid = event?.eventChatCid;
		updateEventDto.autoGenerateEventChat = false;
		updateEventDto.fitnessTypes = event?.fitnessTypes?.length
			? event.fitnessTypes
			: ([{ fitnessTypeId: '', masteryLevel: 0 }] as any[]);
		updateEventDto.updatePrices = event?.prices;
		updateEventDto.onlySingleAttendanceAllowed = event?.onlySingleAttendanceAllowed ?? false;
		updateEventDto.isWaitlistEnabled = event?.isWaitlistEnabled ?? false;
		updateEventDto.eventBundleIds = event?.eventBundles?.map(bundle => bundle.id) ?? [];

		return updateEventDto;
	}

	public static fromEventInstance(eventInstance?: EventInstanceDto) {
		const updateEventDto = this.fromEvent(eventInstance);
		const startDate = eventInstance?.instance ?? updateEventDto.startDate;
		updateEventDto.startDate = startDate ? new Date(startDate) : undefined;

		return updateEventDto;
	}
}

export enum UpdateEventNotificationReason {
	Time = 'time',
	Place = 'place'
}

/** @deprecated Please use CreateEventV2Dto instead */
export class CreateEventDto extends UpdateEventDto {
	@IsAfter('now')
	@IsNotEmpty({
		message: 'Start date should not be empty',
	})
	public override startDate?: Date;

	@ValidateNested()
	@Type(() => CreateScheduleRepeat)
	public override scheduleRepeat?: CreateScheduleRepeat;

	@IsOptional()
	@IsBoolean()
	public isCommunityChatSharingEnabled?: boolean;

	@IsOptional()
	@IsBoolean()
	public allowToPublishToOtherPlatforms?: boolean;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => CreateEventDonationToMembershipDto)
	public createEventDonationToMemberships?: CreateEventDonationToMembershipDto[];
}

export class ScheduleRepeatTypeDto {
	public id: ScheduleRepeatTypesEnum;
	public name: string;
}

export class ScheduleRepeatDto {
	public id: string;
	public interval?: number;
	public typeId: ScheduleRepeatTypesEnum;
	public type: ScheduleRepeatTypeDto;
	public dayOfMonth?: number;
	public weekOfMonth?: number;
	public endDate?: Date;
	public daysOfWeek: DayOfWeekDto[];
}

export class DayOfWeekDto {
	public id: DaysOfWeekEnum;
	public name: string;
	public dayNumber: number;
}

export type EventWithUserAttendanceDto<T extends EventDto = EventDto | EventInstanceDto> = T & {
	currentEventToUser?: Pick<
		EventToUserDto,
		'id' | 'userId' | 'acceptanceOptionId' | 'userRoleId' | 'actualFrom' | 'actualTo'
	>;
	currentWaitlistedUserToEvent?: WaitlistedUserToEventDto;
};

export type EventInstanceWithUserAttendanceDto = EventWithUserAttendanceDto<EventInstanceDto>;

export class EventUsersInvitationDto {
	@IsArray()
	@ArrayMinSize(1)
	userIds: string[];

	@IsOptional()
	@IsString()
	@IsNotEmpty()
	authorId?: string;
}

export class EventUsersCoHostInvitationDto {
	@IsArray()
	@ArrayMaxSize(1)
	@ArrayMinSize(1)
	userIds: string[];

	@IsOptional()
	@IsDateString()
	instance?: Date;

	@IsOptional()
	@IsEnum(EventPeriodType)
	type?: EventPeriodType;
}

export class ExcludeUsersFromEventDto {
	@IsOptional()
	@IsDateString()
	instance?: Date;

	@IsArray()
	@ArrayMinSize(1)
	userIds: string[];
}

export class EventInvitationAnswerDto {
	@IsBoolean()
	isAccepted: boolean;
}

export class EventImageDto {
	public id: string;
	public baseEventId: string;
	public fileId: string;
	public file?: FileDto;
}

export class EventGalleryFileDto {
	public fileKey: string;
	public fileId: string;
	public fileName: string;
}

